import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  type FeatureFace,
  type FeatureLine,
  type FeaturePoint,
} from '../../mesh/feature';
import { markImportedTopologyProxy } from '../../imported-model/selection/imported-topology-proxy-metadata';
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata';
import { ensureImportedModelTopologyPickHost } from '../../imported-model/topology/imported-model-topology-runtime';
import {
  resolveImportedModelTopologyRepository,
} from '../../imported-model/topology/ImportedModelTopologyRepository';
import type { ImportedMeshTopologyBinding } from '../../imported-model/topology/ImportedMeshTopologyBinding';
import { areAssemblyNodeIdentityEquivalent } from '../../assembly/utils/AssemblyNodePath';

/**
 * 稳定装配拓扑引用回查到的 body 记录。
 */
interface ResolvedAssemblyTopologyBodyRecord {
  /** 命中的导入 body 拓扑记录。 */
  bodyRecord: ImportedMeshTopologyBinding;
}

/**
 * 装配体拓扑代理对象。
 */
export type AssemblyTopologyReferenceObject = FeatureFace | FeatureLine | FeaturePoint;

/**
 * 从对象读取装配节点 ID。
 *
 * @param object 场景对象。
 * @returns 装配节点 ID；不存在时返回 `null`。
 */
function resolveAssemblyNodeIdFromObject(object: THREE.Object3D): string | null {
  return ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(object) ?? null;
}

/**
 * 判断对象携带的装配节点身份是否命中目标拓扑引用。
 *
 * @param sourceAssemblyNodeId 对象或 body 选择目标上的装配节点 ID。
 * @param targetAssemblyNodeId Mate 拓扑引用保存的装配节点 ID。
 * @returns 编码、未编码等价身份命中时返回 true。
 */
function isAssemblyNodeIdMatchedTopologyReference(
  sourceAssemblyNodeId: string | null | undefined,
  targetAssemblyNodeId: string,
): boolean {
  return areAssemblyNodeIdentityEquivalent(sourceAssemblyNodeId, targetAssemblyNodeId);
}

/**
 * 判断当前对象是否可能承载指定装配拓扑引用。
 *
 * @param object 场景对象。
 * @param assemblyTopologyRef 装配拓扑稳定引用。
 * @returns 是否匹配该引用所属的装配组件边界。
 */
function isAssemblyTopologyReferenceOwner(
  object: THREE.Object3D,
  assemblyTopologyRef: AssemblyTopologySelectionRef,
): boolean {
  if (
    isAssemblyNodeIdMatchedTopologyReference(
      resolveAssemblyNodeIdFromObject(object),
      assemblyTopologyRef.assemblyNodeId,
    )
  ) {
    return true;
  }

  const bodySelectionTarget = ImportedModelObjectMetadataService
    .resolveBodyBindingSelectionTargetFromSelfOrAncestors(object, assemblyTopologyRef.bodyBindingId);
  /**
   * `.puppy` 重载后，真实 body 可能挂在被折叠的局部显示节点下；
   * 但 Mate 的稳定引用仍指向保存时的可移动组件实例。
   * 因此这里需要允许 bodyBindingId 级覆盖目标命中组件边界。
   */
  return isAssemblyNodeIdMatchedTopologyReference(
    bodySelectionTarget?.assemblyNodeId,
    assemblyTopologyRef.assemblyNodeId,
  );
}

/**
 * 通过稳定装配拓扑引用查找当前 body 记录。
 *
 * @param viewer 视图实例。
 * @param assemblyTopologyRef 装配体拓扑稳定引用。
 * @returns body 回查结果；未命中时返回 `null`。
 */
function resolveAssemblyTopologyBodyRecord(
  viewer: Viewer,
  assemblyTopologyRef: AssemblyTopologySelectionRef,
): ResolvedAssemblyTopologyBodyRecord | null {
  let resolvedRecord: ResolvedAssemblyTopologyBodyRecord | null = null;
  const scene = viewer.scene ?? viewer.getScene?.() ?? null;
  if (!scene) {
    return null;
  }
  scene.traverse((object) => {
    if (resolvedRecord) {
      return;
    }
    if (!isAssemblyTopologyReferenceOwner(object, assemblyTopologyRef)) {
      return;
    }

    const repository = resolveImportedModelTopologyRepository(object);
    const bodyRecord = repository?.resolveBodyRecordByBindingRef(
      assemblyTopologyRef.bodyBindingId,
      { assemblyNodeId: assemblyTopologyRef.assemblyNodeId },
    ) ?? null;
    if (bodyRecord) {
      resolvedRecord = {
        bodyRecord,
      };
    }
  });
  return resolvedRecord;
}

/**
 * 通过稳定装配拓扑引用重新解析当前场景中的代理对象。
 *
 * 设计说明：
 * 1. 导入拓扑代理对象可能被复用或回收，正式基准引用不能长期依赖代理 uuid；
 * 2. 稳定引用通过装配节点、body 绑定和拓扑索引回到当前实例下的 pick host；
 * 3. 返回对象只作为本次求解输入，不写回正式 Datum 对象，避免持久持有代理。
 *
 * @param viewer 视图实例。
 * @param assemblyTopologyRef 装配体拓扑稳定引用。
 * @returns 可参与求解的特征代理对象；失败时返回 `null`。
 */
export function resolveAssemblyTopologyReferenceObject(
  viewer: Viewer,
  assemblyTopologyRef: AssemblyTopologySelectionRef,
): AssemblyTopologyReferenceObject | null {
  const resolvedBodyRecord = resolveAssemblyTopologyBodyRecord(viewer, assemblyTopologyRef);
  if (!resolvedBodyRecord) {
    return null;
  }

  const pickHost = ensureImportedModelTopologyPickHost(resolvedBodyRecord.bodyRecord);
  let resolvedObject: THREE.Object3D | null = null;
  if (assemblyTopologyRef.entityKind === 'face') {
    resolvedObject = pickHost.attachFeatureFaceByIndex(assemblyTopologyRef.topologyIndex);
  } else if (assemblyTopologyRef.entityKind === 'edge') {
    resolvedObject = pickHost.attachFeatureLineByIndex(assemblyTopologyRef.topologyIndex);
  } else if (assemblyTopologyRef.entityKind === 'vertex') {
    resolvedObject = pickHost.attachFeaturePointByIndex(assemblyTopologyRef.topologyIndex);
  }

  if (!resolvedObject) {
    return null;
  }

  markImportedTopologyProxy(resolvedObject);
  /**
   * 配合预览/拖动会先修改组件父级 local transform，再同步拓扑引用。
   * 这里必须刷新父链矩阵，否则 FeatureFace/Line/Point 会短暂携带旧 world matrix，
   * 导致求解输入或命令态高亮与实际实体位置分离。
   */
  resolvedObject.updateWorldMatrix(true, false);
  if (isFeatureFace(resolvedObject) || isFeatureLine(resolvedObject) || isFeaturePoint(resolvedObject)) {
    return resolvedObject;
  }
  return null;
}
